import React, { useEffect, useRef, useCallback } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Undo, Redo, RemoveFormatting } from "lucide-react";
import DOMPurify from "dompurify";

/**
 * Minimaler WYSIWYG-Editor auf contentEditable-Basis.
 * Liefert HTML als String über onChange zurück.
 * Toolbar: Fett, Kursiv, H2, H3, Aufzählung, Nummerierung, Link, Format entfernen, Undo/Redo.
 */

const ALLOWED_TAGS = ["p", "br", "div", "strong", "b", "em", "i", "u", "h2", "h3", "ul", "ol", "li", "a"];
const ALLOWED_ATTR = ["href", "target", "rel"];

export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_ATTR: ["target", "rel"],
  });
}

function exec(cmd, val = null) {
  try {
    document.execCommand(cmd, false, val);
  } catch {
    /* no-op */
  }
}

function ToolbarButton({ onClick, title, active, children, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      data-testid={testid}
      className={`h-8 w-8 inline-flex items-center justify-center text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 transition-colors ${
        active ? "bg-neutral-100 text-neutral-900" : ""
      }`}
    >
      {children}
    </button>
  );
}

function Separator() {
  return <span className="w-px h-5 bg-brand-line mx-1 self-center" />;
}

export default function RichTextEditor({ value, onChange, minHeight = 180, placeholder = "Text eingeben…", testid }) {
  const ref = useRef(null);
  const lastValueRef = useRef("");

  // Only update DOM when external value changes meaningfully (avoid caret jumps)
  useEffect(() => {
    if (!ref.current) return;
    const current = ref.current.innerHTML;
    const incoming = value || "";
    if (incoming !== current && incoming !== lastValueRef.current) {
      ref.current.innerHTML = incoming;
      lastValueRef.current = incoming;
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastValueRef.current = html;
    onChange(html);
  }, [onChange]);

  function format(cmd, val = null) {
    if (ref.current) ref.current.focus();
    exec(cmd, val);
    emit();
  }

  function setBlock(tag) {
    if (ref.current) ref.current.focus();
    exec("formatBlock", tag);
    emit();
  }

  function addLink() {
    const url = window.prompt("Link-URL eingeben (z. B. https://…)", "https://");
    if (!url) return;
    if (ref.current) ref.current.focus();
    exec("createLink", url);
    // Make links open in new tab
    if (ref.current) {
      ref.current.querySelectorAll("a").forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
    }
    emit();
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    exec("insertText", text);
    emit();
  }

  // The editor lives inside a <label> in some admin forms. A <label>'s default
  // activation behavior fires a synthetic click on its first labelable
  // descendant (the Bold <button>), which steals focus from the editor and
  // breaks typing. preventDefault cancels the activation. Attached natively
  // because React synthetic events run after the browser has already processed
  // the label activation. Only neutralize when there is actually a parent
  // <label>, so unrelated click handlers inside the editor are not swallowed.
  const wrapperRef = useRef(null);
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || !node.closest("label")) return;
    const stop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    node.addEventListener("click", stop);
    return () => {
      node.removeEventListener("click", stop);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="border border-brand-line bg-white"
      data-testid={testid}
    >
      <div className="flex items-center flex-wrap gap-0.5 px-1.5 py-1 border-b border-brand-line bg-neutral-50/60">
        <ToolbarButton onClick={() => format("bold")} title="Fett (Ctrl+B)" testid="rte-bold"><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format("italic")} title="Kursiv (Ctrl+I)" testid="rte-italic"><Italic className="h-4 w-4" /></ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => setBlock("H2")} title="Überschrift 2" testid="rte-h2"><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => setBlock("H3")} title="Überschrift 3" testid="rte-h3"><Heading3 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => setBlock("P")} title="Absatz" testid="rte-p">
          <span className="text-xs font-semibold">P</span>
        </ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => format("insertUnorderedList")} title="Aufzählung" testid="rte-ul"><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format("insertOrderedList")} title="Nummerierte Liste" testid="rte-ol"><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <Separator />
        <ToolbarButton onClick={addLink} title="Link einfügen" testid="rte-link"><Link2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format("removeFormat")} title="Formatierung entfernen" testid="rte-clear"><RemoveFormatting className="h-4 w-4" /></ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => format("undo")} title="Rückgängig" testid="rte-undo"><Undo className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format("redo")} title="Wiederholen" testid="rte-redo"><Redo className="h-4 w-4" /></ToolbarButton>
      </div>
      <div
        ref={ref}
        className="rte-content px-3 py-2.5 text-sm focus:outline-none"
        style={{ minHeight }}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        data-testid={testid ? `${testid}-content` : "rte-content"}
      />
    </div>
  );
}
